import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import Link from 'next/link';
import React from 'react';
import { useIntl } from 'react-intl';

import ConstructionImage from '@/assets/group-rv-bookings/construction-site-aerial.jpg';
import CameraImage from '@/assets/group-rv-bookings/red-camera-desert-shoot.jpg';
import RvImage from '@/assets/group-rv-bookings/rv-desert-sunset.jpg';
import SoccerImage from '@/assets/group-rv-bookings/stadium-night-lights.jpg';
import WeddingImage from '@/assets/group-rv-bookings/wedding-couple-hillside.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';

type TResourceCard = {
  key: string;
  image: { src: string };
  title: string;
  linkText: string;
  path: string;
};

const RESOURCE_CARDS: TResourceCard[] = [
  {
    key: 'workforce',
    image: ConstructionImage,
    title: '5 Tips for Coordinating Workforce RV Housing',
    linkText: 'Read the article',
    path: 'https://www.outdoorsy.com/blog/workforce-rv-housing-solutions',
  },
  {
    key: 'rv-power',
    image: RvImage,
    title: 'A Guide to RV Power: Generators vs. Hookups',
    linkText: 'Read the article',
    path: 'https://www.outdoorsy.com/blog/rv-electricity-basics',
  },
  {
    key: 'film-production',
    image: CameraImage,
    title: 'The Ultimate Checklist for Film Production RVs',
    linkText: 'Read the article',
    path: 'https://www.outdoorsy.com/blog/rv-rentals-film-production',
  },
  {
    key: 'soccer',
    image: SoccerImage,
    title: 'Everything Soccer Fans Need To Know',
    linkText: 'Resources for World Soccer',
    path: 'https://www.outdoorsy.com/2026-world-soccer',
  },
  {
    key: 'weddings',
    image: WeddingImage,
    title: 'Why Choose RV Rentals For Outdoor Weddings?',
    linkText: 'Read the article',
    path: 'https://www.outdoorsy.com/blog/group-rv-rentals-weddings',
  },
];

export const GroupRvResources: React.FC = () => {
  const intl = useIntl();

  return (
    <section className="py-12 bg-white  lg:py-16">
      <div className="flex justify-center lg:px-12">
        <MaxWidth className="gap-10">
          {/* Section heading */}
          <Heading
            variant={EHeadingStyleVariant.H3}
            className="leading-tight text-left text-gray-900 font-[abril-titling] !text-[35px] sm:!text-[32px] md:!text-[40px] ">
            {intl.formatMessage({
              id: 'gPldKv',
              defaultMessage: 'Everything you need to know about Group Bookings.',
              description: 'Group bookings resources section heading',
            })}
          </Heading>

          {/* Cards - horizontal scroll on mobile, grid on desktop */}
          <div className="flex pb-2 overflow-x-auto gap-4 md:grid md:grid-cols-5 md:gap-6 md:overflow-visible">
            {RESOURCE_CARDS.map(card => (
              <div
                key={card.key}
                className="flex flex-col flex-shrink-0 h-full overflow-hidden bg-white rounded-2xl md:flex-shrink md:w-full w-[60vw] sm:w-[60vw]  ">
                {/* Image */}
                <div className="relative overflow-hidden md:w-full w-[60vw] sm:w-[60vw] rounded-2xl aspect-[4/5]">
                  <ResponsiveImage
                    src={card.image.src}
                    alt={card.title}
                    className="object-cover w-full h-full transition duration-300 ease-in-out hover:scale-105 hover:brightness-110"
                  />
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-2 bg-white">
                  <Text
                    variant={ETextStyleVariant.SmallBold}
                    className="text-[#212831] text-[17px] leading-[24px] sm:text-[16px] sm:leading-[26px] md:text-[17px] md:leading-[26px]">
                    {card.title}
                  </Text>

                  <Link
                    href={card.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto text-left">
                    <span className="text-sm font-medium text-green-500 underline underline-offset-2 hover:no-underline">
                      {card.linkText}
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </MaxWidth>
      </div>
    </section>
  );
};
